import type { AdminAccount, AdminProfile } from '../../types'
import {
  BackofficeApiError,
  requestAuthed,
  resolveTokenFromAuthHeaders,
  toErrorMessage,
  type LegacyAuthHeaders,
} from '../../lib/api/http'

type Translator = (key: string, params?: Record<string, string | number>) => string

export const requestProfileSnapshot = async ({
  authHeaders,
  t,
}: {
  authHeaders: LegacyAuthHeaders
  t: Translator
}): Promise<{ profile?: Partial<AdminProfile>; account: AdminAccount | null }> => {
  const token = resolveTokenFromAuthHeaders(authHeaders)
  const { response, payload } = await requestAuthed({
    token,
    path: '/admin/profile',
  })
  if (!response.ok) {
    throw new BackofficeApiError({
      message: t('profile.toast.loadError'),
      status: response.status,
      path: '/admin/profile',
      payload,
    })
  }

  const data = payload as { profile?: Partial<AdminProfile>; account?: AdminAccount | null }
  return {
    profile: data?.profile,
    account: data?.account ?? null,
  }
}

export const requestProfileUpdate = async ({
  authHeaders,
  payload,
  t,
}: {
  authHeaders: LegacyAuthHeaders
  payload: Partial<AdminProfile>
  t: Translator
}): Promise<{ profile?: Partial<AdminProfile>; account?: AdminAccount }> => {
  const token = resolveTokenFromAuthHeaders(authHeaders)
  const { response, payload: body } = await requestAuthed({
    token,
    path: '/admin/profile',
    init: {
      method: 'PATCH',
      headers: authHeaders(),
      body: JSON.stringify(payload),
    },
  })
  if (!response.ok) {
    throw new Error(toErrorMessage(body, t('profile.toast.updateError')))
  }

  const data = body as { profile?: Partial<AdminProfile>; account?: AdminAccount }
  return {
    profile: data?.profile,
    account: data?.account,
  }
}
